/**
 * «Pendiente de ti» — GEN-EKB-024 (D55). Bajo la cabecera de Saber, solo si hay alguno: lo que Jimmy preparó por
 * encargo de otra persona y espera la confirmación de QUIEN ESTÁ MIRANDO (titular de un KB, administrador).
 * Mockup: Saber-2-nube.html, fila de pendiente del bloque «Jimmy esta semana» (.enc .row, .st, .q small).
 * El kernel decide qué pendientes son de esta persona (GET /v1/ekb/mis-pendientes) y confirmar ejecuta con SU
 * identidad; aquí solo se pintan y se resuelven. También se resuelven diciéndoselo a Jimmy (el chat refresca Saber).
 * GEN-PX-020: se unen en la misma lista las rebajas de etiqueta y las quitas de iKu que esperan al titular
 * (GEN-EKB-010, GET /v1/ekb/pendientes). Cada fila lleva su `tipo` para elegir la llamada que la resuelve.
 */
import { useCallback, useEffect, useState } from 'react'
import { listMisPendientes, listPendientes, resolverCambioEtiqueta, resolverPendiente, resolverQuita } from '../api/client.js'
import { textoCambio, textoQuita } from './PendientePanel.jsx'

const fecha = iso => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '')

export default function SaberPendientes({ refresco = 0, onResuelto }) {
  const [filas, setFilas] = useState([])
  const [ocupado, setOcupado] = useState(null)
  const [error, setError] = useState(null) // { id, texto }

  const cargar = useCallback(async () => {
    const seguro = async f => { try { return await f() } catch { return null } }
    const [jimmy, titular] = await Promise.all([seguro(() => listMisPendientes()), seguro(() => listPendientes())])
    return [
      ...(jimmy?.pendientes ?? []).map(p => ({ ...p, via: 'jimmy' })),
      ...(titular?.pendientes ?? []).map(p => ({ ...p, via: 'etiqueta', resumen: textoCambio(p), agente: null })),
      ...(titular?.quitas ?? []).map(q => ({ ...q, via: 'quita', resumen: textoQuita(q), agente: null })),
    ]
  }, [])

  useEffect(() => {
    let vivo = true
    cargar().then(f => vivo && setFilas(f))
    return () => { vivo = false }
  }, [refresco, cargar])

  async function resolver(p, confirmar) {
    setOcupado(p.id)
    setError(null)
    try {
      if (p.via === 'etiqueta') await resolverCambioEtiqueta(p.id, confirmar)
      else if (p.via === 'quita') await resolverQuita(p.id, confirmar)
      else await resolverPendiente(p.id, confirmar)
      setFilas(await cargar())
      await onResuelto?.()
    } catch (e) {
      setError({ id: p.id, texto: e?.message || 'No se ha podido resolver.' })
      setFilas(await cargar())
    } finally {
      setOcupado(null)
    }
  }

  if (!filas.length) return null
  return (
    <section className="dk-pend" aria-label="Pendiente de ti">
      <div className="dk-pend__cab"><h2>Pendiente de ti</h2><span className="dk-pend__n">{filas.length}</span></div>
      <div className="dk-pend__lista">
        {filas.map(p => (
          <div className="dk-pend__fila" key={`${p.via}:${p.id}`}>
            <span className="dk-pend__st" aria-hidden="true">!</span>
            <div className="dk-pend__q">
              {p.resumen}
              <small>
                {p.via === 'jimmy'
                  ? `${p.agente ?? 'Jimmy'} por encargo de ${p.solicitante?.display_name ?? p.solicitante?.email} · lo confirmas tú · ${fecha(p.creado_en)}`
                  : `Propuesto por ${p.propuesto_por?.display_name ?? ''} · lo confirmas tú como titular · ${fecha(p.propuesto_en)}`}
              </small>
              {error?.id === p.id && <small role="alert" className="dk-pend__err">{error.texto}</small>}
            </div>
            <div className="dk-pend__acts">
              <button type="button" className="dk-btn dk-btn--primario" disabled={ocupado === p.id} onClick={() => resolver(p, true)}>Confirmar</button>
              <button type="button" className="dk-btn" disabled={ocupado === p.id} onClick={() => resolver(p, false)}>Rechazar</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
